/**
 * Theme initialization utilities
 */

import { REDUCED_MOTION_MQ } from './breakpoints.js'

/**
 * Initialize core theme functionality
 * Returns cleanup function to remove all event listeners
 */
export function initializeTheme () {
  // Smooth scrolling for anchor links
  const cleanupSmoothScroll = setupSmoothScrolling()

  // CookieYes modal trigger
  const cleanupCookieYes = setupCookieYesTrigger()

  // Correct scroll position when page loads with a hash in the URL
  setupScrollPaddingTop()

  // Return cleanup function
  return {
    destroy () {
      cleanupSmoothScroll()
      cleanupCookieYes()
    }
  }
}

/**
 * Sets scroll-padding-top on <html> to match the sticky header height.
 *
 * This is the standard CSS mechanism for hash-link offset: the browser's
 * native hash scroll (on page load, refresh, and back/forward) automatically
 * respects scroll-padding-top, so the target element lands just below the
 * header with no JS timing hacks required.
 *
 * Also handles resize so the value stays correct if the header height changes
 * (e.g. desktop vs mobile breakpoint switch).
 */
function setupScrollPaddingTop () {
  const update = () => {
    const siteHeader = document.querySelector('.site-header')
    const headerHeight = siteHeader ? siteHeader.offsetHeight : 0
    document.documentElement.style.scrollPaddingTop = headerHeight + 'px'
  }

  update()

  // Debounce resize to avoid forcing layout on every pixel of resize
  let resizeTimer
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(update, 100)
  }, { passive: true })
}

/**
 * Setup smooth scrolling for anchor links
 *
 * Excludes links with:
 * - .no-smooth-scroll class (on link or parent element)
 * - data-no-smooth-scroll attribute (on link or parent element)
 * - href="#" only (no target ID)
 *
 * Use cases for exclusion:
 * - Modal triggers (CookieYes, custom modals)
 * - JavaScript-driven interactions
 * - Links that should maintain default browser behavior
 *
 * Note: Uses capture phase to run before Elementor's smooth scroll
 * Returns cleanup function to remove event listeners
 */
function setupSmoothScrolling () {
  // Match both pure hash links (#anchor) and full URLs containing a hash.
  const anchors = document.querySelectorAll('a[href^="#"], a[href*="#"]')
  const handlers = new WeakMap()

  anchors.forEach(anchor => {
    const handleClick = (e) => {
      let href = anchor.getAttribute('href')

      // For full URLs containing a hash, check if they point to the current page.
      // If so, treat them as in-page anchors.
      if (href && !href.startsWith('#')) {
        try {
          const linkUrl = new URL(href, window.location.href)
          if (linkUrl.origin === window.location.origin &&
              linkUrl.pathname === window.location.pathname &&
              linkUrl.hash) {
            href = linkUrl.hash // Reduce to just #fragment
          } else {
            return // Different page — let the browser handle it normally
          }
        } catch {
          return
        }
      }

      // Skip excluded links entirely (let them behave naturally)
      if (anchor.classList.contains('no-smooth-scroll') ||
          anchor.hasAttribute('data-no-smooth-scroll') ||
          anchor.closest('.no-smooth-scroll') ||
          anchor.closest('[data-no-smooth-scroll]')) {
        return
      }

      // Skip links with just "#" (no target) - let them behave naturally
      if (href === '#') {
        return
      }

      // Apply smooth scrolling for valid anchor links
      const target = document.querySelector(href)
      if (target) {
        e.preventDefault()

        // scrollIntoView respects the scroll-padding-top set on <html>,
        // so the sticky header offset is handled in one place.
        // Check for reduced motion preference
        const prefersReducedMotion = REDUCED_MOTION_MQ.matches

        target.scrollIntoView({
          behavior: prefersReducedMotion ? 'instant' : 'smooth',
          block: 'start'
        })
      }
    }

    handlers.set(anchor, handleClick)
    anchor.addEventListener('click', handleClick, true) // Capture phase - runs before Elementor
  })

  // Return cleanup function
  return () => {
    anchors.forEach(anchor => {
      const handler = handlers.get(anchor)
      if (handler) {
        anchor.removeEventListener('click', handler, true)
      }
    })
  }
}

/**
 * Setup CookieYes modal trigger
 *
 * Prevents default scroll behavior and triggers CookieYes modal
 * Based on working implementation from previous project
 * Returns cleanup function to remove event listeners
 */
function setupCookieYesTrigger () {
  const links = document.querySelectorAll('.cky-banner-element')
  const handlers = new WeakMap()

  links.forEach((link) => {
    const handleClick = (e) => {
      e.preventDefault() // Prevent default scroll behavior

      // Trigger CookieYes modal if available
      // eslint-disable-next-line no-undef
      if (typeof CookieYes !== 'undefined' && typeof CookieYes.showConsentSettings === 'function') {
        // eslint-disable-next-line no-undef
        CookieYes.showConsentSettings()
      }
    }

    handlers.set(link, handleClick)
    link.addEventListener('click', handleClick)
  })

  // Return cleanup function
  return () => {
    links.forEach((link) => {
      const handler = handlers.get(link)
      if (handler) {
        link.removeEventListener('click', handler)
      }
    })
  }
}
